// ABOUTME: Member picker for virtual shops: per-shop checkbox, claimed category subset,
// ABOUTME: and host shop selection. Empty category selection means "all categories".

import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';

export default function VirtualMembersEditor({ candidateShops, categories, members, hostShopId, onChange }) {
  const { t } = useTranslation();

  const memberById = new Map((members || []).map(member => [member.shop_id, member]));

  const emitChange = (nextMembers, nextHost) => {
    const memberIds = new Set(nextMembers.map(member => member.shop_id));
    onChange({
      members: nextMembers,
      host_shop_id: nextHost && memberIds.has(nextHost) ? nextHost : null,
    });
  };

  const toggleMember = (shopId) => {
    if (memberById.has(shopId)) {
      emitChange((members || []).filter(member => member.shop_id !== shopId), hostShopId);
    } else {
      emitChange([...(members || []), { shop_id: shopId, categories: null }], hostShopId);
    }
  };

  const setMemberCategories = (shopId, selected) => {
    const nextMembers = (members || []).map(member =>
      member.shop_id === shopId
        ? { ...member, categories: selected.length > 0 ? selected : null }
        : member
    );
    emitChange(nextMembers, hostShopId);
  };

  const setHost = (shopId) => {
    emitChange(members || [], shopId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.text.primary }}>
          {t('config.shop.members')}
        </Typography>
        <FormControlLabel
          control={<Radio size="small" checked={!hostShopId} onChange={() => setHost(null)} />}
          label={<Typography sx={{ fontSize: '0.75rem', color: colors.text.secondary }}>{t('config.shop.noHost')}</Typography>}
        />
      </Box>

      {(candidateShops || []).map(shop => {
        const member = memberById.get(shop.shop_id);
        const isMember = !!member;
        return (
          <Box
            key={shop.shop_id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: '8px',
              border: `1px solid ${isMember ? colors.palette.blue : colors.border.light}`,
              bgcolor: isMember ? '#eff6ff' : 'transparent',
            }}
          >
            <Checkbox
              size="small"
              checked={isMember}
              onChange={() => toggleMember(shop.shop_id)}
            />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: colors.text.primary, minWidth: 90 }}>
              {shop.name || shop.shop_id}
            </Typography>

            {isMember && (
              <>
                <Select
                  multiple
                  size="small"
                  displayEmpty
                  value={member.categories || []}
                  onChange={(e) => setMemberCategories(shop.shop_id, e.target.value)}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <Typography sx={{ fontSize: '0.75rem', color: colors.text.muted }}>
                        {t('config.shop.allCategories')}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(id => {
                          const category = (categories || []).find(cat => cat.id === id);
                          return <Chip key={id} label={category?.name || id} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />;
                        })}
                      </Box>
                    )
                  }
                  sx={{ flexGrow: 1, borderRadius: '8px', fontSize: '0.8rem' }}
                >
                  {(categories || []).map(category => (
                    <MenuItem key={category.id} value={category.id} sx={{ fontSize: '0.8rem' }}>
                      {category.name || category.id}
                    </MenuItem>
                  ))}
                </Select>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Radio
                      size="small"
                      checked={hostShopId === shop.shop_id}
                      onChange={() => setHost(shop.shop_id)}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.75rem', color: colors.text.secondary }}>{t('config.shop.hostShop')}</Typography>}
                />
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
